import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuDownload, LuFileText, LuLink } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import { ErrorState, FormDrawer, LoadingState } from '../ui';
import '../../styles/source-reference.css';

// Backend appends e.g. "\n\n🔗 Source Reference: [CHK-0653F8]" to story descriptions
const SOURCE_REFERENCE_PATTERN = /\s*(?:🔗\s*)?Source Reference:\s*\[([^\]\s]+)\]\s*/iu;

/**
 * Splits the source reference out of a story description.
 * @returns {{ referenceId: string|null, description: string }}
 */
export const parseSourceReference = (description) => {
  const text = String(description || '');
  const match = text.match(SOURCE_REFERENCE_PATTERN);
  if (!match) return { referenceId: null, description: text };
  return {
    referenceId: match[1],
    description: text.replace(SOURCE_REFERENCE_PATTERN, ' ').trim(),
  };
};

const EXTENSION_BY_MIME = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'text/plain': 'txt',
};

// S3 keys may lack an extension, so derive one from the downloaded file type when missing
const buildDownloadName = (chunk, mimeType) => {
  const baseName = chunk.document_name || chunk.s3_url.split('/').pop() || chunk.reference_id || 'source';
  const extension = EXTENSION_BY_MIME[mimeType];
  return extension && !/\.[a-z0-9]{2,5}$/i.test(baseName) ? `${baseName}.${extension}` : baseName;
};

const SourceChunkDrawer = ({ referenceId, onClose }) => {
  const [chunk, setChunk] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!chunk?.s3_url) return;
    setIsDownloading(true);
    try {
      // Plain fetch: S3 must not receive the app's auth headers
      const response = await fetch(chunk.s3_url);
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = buildDownloadName(chunk, blob.type);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      // CORS-blocked or failed fetch: let the browser download the file directly
      console.warn('Blob download failed, opening source URL instead:', err);
      window.open(chunk.s3_url, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  const fetchChunk = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/document/chunks/${encodeURIComponent(referenceId)}`);
      if (response && response.status_code === 200 && response.data) {
        setChunk(response.data);
      } else {
        throw new Error(response?.message || 'Failed to fetch source reference');
      }
    } catch (err) {
      console.error('Error fetching source reference:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch source reference');
    } finally {
      setIsLoading(false);
    }
  }, [referenceId]);

  useEffect(() => {
    fetchChunk();
  }, [fetchChunk]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <FormDrawer
      open
      wide
      onClose={onClose}
      title="Source Reference"
      footer={
        <>
          {chunk?.s3_url && (
            <button
              type="button"
              className="rt-btn rt-btn--secondary"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <LuDownload size={14} />
              {isDownloading ? 'Downloading…' : 'Download source'}
            </button>
          )}
          <button type="button" className="rt-btn rt-btn--primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {isLoading && <LoadingState label="Loading source reference…" />}

      {!isLoading && error && (
        <ErrorState title="Unable to load source reference" description={error} onRetry={fetchChunk} />
      )}

      {!isLoading && !error && chunk && (
        <div className="rt-source-chunk">
          <dl className="rt-source-chunk__meta">
            <div>
              <dt>Reference</dt>
              <dd>
                <span className="rt-source-ref rt-source-ref--static">
                  <LuLink size={11} />
                  {chunk.reference_id || referenceId}
                </span>
              </dd>
            </div>
            <div>
              <dt>Document</dt>
              <dd className="rt-source-chunk__doc">
                <LuFileText size={14} />
                <span title={chunk.document_name}>{chunk.document_name || '—'}</span>
              </dd>
            </div>
          </dl>

          <div className="rt-source-chunk__label">Source text</div>
          <pre className="rt-source-chunk__text">{chunk.source_text || 'No source text available.'}</pre>
        </div>
      )}
    </FormDrawer>,
    document.body
  );
};

/**
 * Clickable badge for a story's source reference; opens the source chunk in a drawer.
 */
export const SourceReferenceBadge = ({ referenceId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClose = useCallback(() => setIsOpen(false), []);

  if (!referenceId) return null;

  return (
    <>
      <button
        type="button"
        className="rt-source-ref"
        title={`View source reference ${referenceId}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        <LuLink size={11} />
        {referenceId}
      </button>
      {isOpen && <SourceChunkDrawer referenceId={referenceId} onClose={handleClose} />}
    </>
  );
};
